import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Handshake, Building2, TrendingUp, ShieldCheck, Wallet, Wrench, FileCheck2,
  Send, Loader2, ArrowRight, Car, BadgeCheck, Clock, PhoneCall, User, ChevronLeft,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import { useReveal } from "@/hooks/useReveal";
import Seo from "@/components/Seo";
import { Footer } from "@/App";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_IMG = "/img/n148kmw9_image.webp";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/212a1033-4c21-4834-be3e-c63c797e7917/images/46be147e069f191329f60f34251c4faaa5ca9f04c741ef517b442b07eab47e51.png";
const B2B_IMG = "/img/3p72u9i9_K1.webp";
const OWNER_IMG = "/img/c7gj2k4g_XC_F1.webp";

const submitPartnership = async (payload) => {
  const res = await fetch(`${BACKEND_URL}/api/partnership`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error("Nepodařilo se odeslat, zkuste to prosím znovu.");
};

const PageNav = () => (
  <nav className="fixed top-0 left-0 right-0 z-50 nav-glass" data-testid="spoluprace-nav">
    <div className="max-w-7xl mx-auto px-4 sm:px-6">
      <div className="flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-2 sm:gap-3" data-testid="spoluprace-logo">
          <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
          <span className="font-['Cabinet_Grotesk'] font-bold text-lg sm:text-xl tracking-wide">EFFECTDEAL</span>
        </Link>
        <div className="flex items-center gap-3 sm:gap-5">
          <a href="/#vehicles" className="hidden sm:inline text-sm font-medium text-zinc-400 hover:text-white transition-colors">Vozy</a>
          <a href="/#contact" className="hidden sm:inline text-sm font-medium text-zinc-400 hover:text-white transition-colors">Kontakt</a>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-300 hover:text-white transition-colors" data-testid="spoluprace-back">
            <ChevronLeft className="w-4 h-4" /> Domů
          </Link>
          <a href="/prihlaseni" className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-200 border border-white/15 hover:border-orange-400/60 rounded-full px-4 py-2 transition-colors">
            <User className="w-4 h-4" /> Klientská zóna
          </a>
        </div>
      </div>
    </div>
  </nav>
);

const Bento = ({ icon: Icon, title, text, className = "" }) => (
  <div className={`glass rounded-2xl p-6 hover:border-orange-400/30 transition-colors ${className}`}>
    <div className="w-11 h-11 rounded-xl bg-orange-400/15 flex items-center justify-center mb-4">
      <Icon className="w-5 h-5 text-orange-400" />
    </div>
    <h3 className="font-['Cabinet_Grotesk'] text-lg font-bold mb-1.5">{title}</h3>
    <p className="text-zinc-400 text-sm leading-relaxed">{text}</p>
  </div>
);

const Step = ({ n, title, text }) => (
  <div className="flex gap-4">
    <div className="w-9 h-9 shrink-0 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 text-black font-bold flex items-center justify-center">{n}</div>
    <div>
      <h4 className="font-semibold text-zinc-100">{title}</h4>
      <p className="text-zinc-400 text-sm">{text}</p>
    </div>
  </div>
);

// ---------- B2B form ----------
const B2BForm = () => {
  const [f, setF] = useState({ company: "", vehicleCount: "", vehicleTypes: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitPartnership({ ...f, vehicleTypes: `[FIRMY/B2B] ${f.vehicleTypes || ""}`.trim() });
      toast.success("Děkujeme! Ozveme se vám s nabídkou pro firmy.");
      setF({ company: "", vehicleCount: "", vehicleTypes: "", contact: "" });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-3" data-testid="b2b-form">
      <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold mb-1">Nezávazná poptávka pro firmy</h3>
      <p className="text-zinc-500 text-sm mb-3">Připravíme vám flotilovou nabídku na míru.</p>
      <input className="input-dark" placeholder="Název firmy *" required value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="b2b-company" />
      <input className="input-dark" placeholder="Počet vozů (např. 3–10) *" required value={f.vehicleCount} onChange={(e) => setF({ ...f, vehicleCount: e.target.value })} data-testid="b2b-count" />
      <input className="input-dark" placeholder="Jaké vozy / k čemu (volitelné)" value={f.vehicleTypes} onChange={(e) => setF({ ...f, vehicleTypes: e.target.value })} data-testid="b2b-types" />
      <input className="input-dark" placeholder="Kontakt (telefon nebo e-mail) *" required value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="b2b-contact" />
      <button type="submit" disabled={busy} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="b2b-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Odeslat poptávku
      </button>
    </form>
  );
};

// ---------- Owner (passive income) form ----------
const OwnerForm = () => {
  const [f, setF] = useState({ name: "", car: "", value: "", contact: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitPartnership({
        company: f.name,
        vehicleCount: f.car,
        vehicleTypes: `[VLASTNÍK VOZU – pasivní příjem] hodnota vozu: ${f.value || "neuvedeno"}`,
        contact: f.contact,
      });
      toast.success("Děkujeme! Ozveme se vám, jak může vaše auto vydělávat.");
      setF({ name: "", car: "", value: "", contact: "" });
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-3" data-testid="owner-form">
      <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold mb-1">Chci, ať mé auto vydělává</h3>
      <p className="text-zinc-500 text-sm mb-3">Spočítáme vám nezávazně potenciální příjem.</p>
      <input className="input-dark" placeholder="Jméno a příjmení *" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="owner-name" />
      <input className="input-dark" placeholder="Vozidlo (značka, model, rok) *" required value={f.car} onChange={(e) => setF({ ...f, car: e.target.value })} data-testid="owner-car" />
      <input className="input-dark" placeholder="Přibližná hodnota vozu (Kč)" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} data-testid="owner-value" />
      <input className="input-dark" placeholder="Kontakt (telefon nebo e-mail) *" required value={f.contact} onChange={(e) => setF({ ...f, contact: e.target.value })} data-testid="owner-contact" />
      <button type="submit" disabled={busy} className="btn-accent w-full inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="owner-submit">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Spočítat můj příjem
      </button>
    </form>
  );
};

const czk = (n) => Math.round(n).toLocaleString("cs-CZ") + " Kč";

// Orientační kalkulačka výnosu pro majitele vozu.
const EarningsCalculator = () => {
  const [value, setValue] = useState(700000);
  const monthly = Math.round((value * 0.018) / 100) * 100; // ~1,8 % hodnoty / měsíc
  const yearly = monthly * 12;
  return (
    <div className="glass rounded-2xl p-6 sm:p-8 mb-12 reveal" data-testid="earnings-calculator">
      <div className="flex items-center gap-2 mb-1">
        <Wallet className="w-5 h-5 text-orange-400" />
        <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold">Kalkulačka výnosu</h3>
      </div>
      <p className="text-zinc-500 text-sm mb-6">Posuňte hodnotu vozu a uvidíte orientační měsíční příjem.</p>
      <div className="grid md:grid-cols-2 gap-8 items-center">
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-zinc-400">Hodnota vozu</span>
            <span className="font-bold text-orange-400" data-testid="calc-value">{czk(value)}</span>
          </div>
          <input
            type="range" min="200000" max="2000000" step="50000"
            value={value}
            onChange={(e) => setValue(Number(e.target.value))}
            className="w-full accent-orange-500 cursor-pointer"
            data-testid="calc-slider"
          />
          <div className="flex justify-between text-[11px] text-zinc-600 mt-1">
            <span>200 tis.</span><span>2 mil.</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-4 text-center">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400">Měsíčně</p>
            <p className="text-2xl font-bold text-orange-400" data-testid="calc-monthly">{czk(monthly)}</p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-center">
            <p className="text-[11px] uppercase tracking-wider text-zinc-400">Ročně</p>
            <p className="text-2xl font-bold text-zinc-100" data-testid="calc-yearly">{czk(yearly)}</p>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-zinc-600 mt-5">* Pouze orientační odhad. Konkrétní výnos závisí na typu, stavu a vytíženosti vozu — spočítáme vám ho přesně po nezávazné poptávce.</p>
    </div>
  );
};

const SpolupracePage = () => {
  useReveal();
  const [branch, setBranch] = useState("firmy");

  return (
    <div className="min-h-screen bg-[#08080A]">
      <Seo
        title="Spolupráce a partnerství | EffectDeal"
        description="Spolupracujte s EffectDeal. Flotilový pronájem pro firmy a B2B, nebo nechte své auto vydělávat jako pasivní příjem. Nezávazná poptávka online."
        path="/spoluprace"
      />
      <PageNav />

      {/* Hero */}
      <section className="relative min-h-[72vh] flex items-center overflow-hidden" data-testid="spoluprace-hero">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_IMG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08080A]/80 via-[#08080A]/85 to-[#08080A]" />
        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 pt-28 pb-16 text-center">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-4 reveal">
            <Handshake className="w-4 h-4" /> Spolupráce &amp; partnerství
          </div>
          <h1 className="font-['Cabinet_Grotesk'] font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight reveal reveal-delay-1">
            Partnerství, které <span className="text-orange-400">dává smysl</span>
          </h1>
          <p className="text-zinc-300 text-base sm:text-lg max-w-2xl mx-auto mt-5 reveal reveal-delay-2">
            Ať jste firma hledající chytré řešení mobility, nebo majitel vozu, který chce, aby mu auto vydělávalo — máme pro vás cestu.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-8 reveal reveal-delay-3">
            <a href="#firmy" className="btn-accent w-full sm:w-auto" data-testid="hero-cta-firmy">Pro firmy / B2B</a>
            <a href="#vlastnici" className="btn-outline w-full sm:w-auto" data-testid="hero-cta-vlastnici">Ať mi auto vydělává</a>
          </div>
        </div>
      </section>

      {/* Branch switcher */}
      <div className="sticky top-16 sm:top-20 z-40 bg-[#08080A]/90 backdrop-blur-md border-y border-white/5">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 flex gap-2 py-3">
          {[
            { id: "firmy", label: "Pro firmy / B2B", icon: Building2, href: "#firmy" },
            { id: "vlastnici", label: "Ať vám auto vydělává", icon: TrendingUp, href: "#vlastnici" },
          ].map((b) => (
            <a
              key={b.id}
              href={b.href}
              onClick={() => setBranch(b.id)}
              className={`flex-1 inline-flex items-center justify-center gap-2 text-sm font-semibold py-2.5 rounded-full border transition-colors ${branch === b.id ? "bg-orange-400 text-black border-orange-400" : "border-white/10 text-zinc-300 hover:border-white/25"}`}
              data-testid={`branch-tab-${b.id}`}
            >
              <b.icon className="w-4 h-4" /> {b.label}
            </a>
          ))}
        </div>
      </div>

      {/* Branch A: B2B */}
      <section id="firmy" className="py-16 sm:py-24 px-4 sm:px-6 scroll-mt-32" data-testid="branch-firmy">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center mb-12 reveal">
            <div>
              <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs mb-3">
                <Building2 className="w-4 h-4" /> Pro firmy
              </div>
              <h2 className="section-title text-3xl sm:text-4xl mb-4">Flotila bez starostí</h2>
              <p className="text-zinc-400 leading-relaxed">
                Dlouhodobý pronájem vozů pro firmy s předvídatelnými náklady. Žádné vázání kapitálu v autech, žádné starosti se servisem, pojištěním ani prodejem. Vy se věnujete byznysu — o vozy se postaráme my.
              </p>
            </div>
            <div className="rounded-2xl overflow-hidden aspect-[4/3] border border-white/10">
              <img src={B2B_IMG} alt="Firemní flotila vozů" className="w-full h-full object-cover" />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 reveal reveal-delay-1">
            <Bento icon={Wallet} title="Fixní náklady" text="Jasná měsíční platba včetně pojištění, servisu a dálniční známky." />
            <Bento icon={Wrench} title="Bez údržby" text="Servis, pneu i administrativu řešíme za vás." />
            <Bento icon={Car} title="Vozy na míru" text="Vybereme vozy přesně podle potřeb vaší firmy a rozpočtu." />
            <Bento icon={Clock} title="Flexibilita" text="Pronájem už od 1 měsíce, navýšení flotily kdykoli." />
          </div>

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-6 reveal">
              <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold">Jak spolupráce probíhá</h3>
              <Step n="1" title="Poptávka" text="Řeknete nám, kolik vozů a na co potřebujete." />
              <Step n="2" title="Nabídka na míru" text="Připravíme řešení a transparentní ceník." />
              <Step n="3" title="Smlouva online" text="Smlouvu podepíšete digitálně, bez papírování." />
              <Step n="4" title="Předání vozů" text="Vozy připravené k provozu — pojištěné a se servisem." />
            </div>
            <div className="reveal reveal-delay-1"><B2BForm /></div>
          </div>
        </div>
      </section>

      {/* Branch B: Passive income */}
      <section id="vlastnici" className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0a0a0a] scroll-mt-32" data-testid="branch-vlastnici">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 items-center mb-12 reveal">
            <div className="rounded-2xl overflow-hidden aspect-[4/3] border border-white/10 order-2 lg:order-1">
              <img src={OWNER_IMG} alt="Předání klíčů — spolupráce" className="w-full h-full object-cover" />
            </div>
            <div className="order-1 lg:order-2">
              <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs mb-3">
                <TrendingUp className="w-4 h-4" /> Pro majitele vozů
              </div>
              <h2 className="section-title text-3xl sm:text-4xl mb-4">Ať vám auto vydělává</h2>
              <p className="text-zinc-400 leading-relaxed">
                Máte vůz, který jen stojí a ztrácí na hodnotě? Svěřte ho nám. Postaráme se o jeho pronájem, správu i administrativu a vy získáte <span className="text-orange-400 font-semibold">pasivní příjem</span> — bez práce a starostí.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12 reveal reveal-delay-1">
            <Bento icon={TrendingUp} title="Pasivní příjem" text="Vaše auto vydělává místo toho, aby jen stálo a ztrácelo hodnotu." />
            <Bento icon={ShieldCheck} title="Pojištění & garance" text="Vůz je po celou dobu pojištěný a pod naší správou." />
            <Bento icon={FileCheck2} title="Vše vyřídíme" text="Smlouvy, klienty i servis řešíme kompletně za vás." />
            <Bento icon={BadgeCheck} title="Transparentnost" text="Jasné podmínky a přehled o příjmech z vašeho vozu." />
          </div>

          <EarningsCalculator />

          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div className="space-y-6 reveal">
              <h3 className="font-['Cabinet_Grotesk'] text-xl font-bold">Jak to funguje</h3>
              <Step n="1" title="Ozvete se nám" text="Pošlete základní info o voze přes formulář." />
              <Step n="2" title="Vyhodnocení" text="Spočítáme potenciální měsíční příjem a podmínky." />
              <Step n="3" title="Předání do správy" text="Vůz převezmeme a zařadíme do pronájmu." />
              <Step n="4" title="Vyděláváte" text="Pravidelně vám vyplácíme příjem z pronájmu vozu." />
            </div>
            <div className="reveal reveal-delay-1"><OwnerForm /></div>
          </div>
        </div>
      </section>

      {/* Partners / trust band */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 border-t border-white/5" data-testid="partner-logos">
        <div className="max-w-6xl mx-auto">
          <div className="glass rounded-3xl overflow-hidden grid lg:grid-cols-2 reveal">
            {/* White Kodiaq */}
            <div className="relative min-h-[260px] lg:min-h-[380px]">
              <img src={B2B_IMG} alt="Bílá Škoda Kodiaq EffectDeal" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-[#08080A] via-transparent to-transparent" />
            </div>
            {/* Copy + partner logo */}
            <div className="p-8 sm:p-12 flex flex-col justify-center">
              <p className="text-orange-400 text-sm uppercase tracking-widest font-semibold mb-3">Důvěřují nám</p>
              <h2 className="font-['Cabinet_Grotesk'] font-black text-2xl sm:text-3xl mb-3">Spolupracujeme s&nbsp;předními partnery</h2>
              <p className="text-zinc-400 text-sm sm:text-base leading-relaxed mb-7">
                Společně přinášíme férový a moderní pronájem vozů. Kvalitní vozový park, spolehlivý servis a partneři, na které se můžete spolehnout.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <div className="bg-white rounded-xl px-6 py-4 inline-flex items-center justify-center shadow-lg" data-testid="partner-logo-aspekta">
                  <img src="/img/aspekta-logo.webp" alt="Aspekta Trading" className="h-12 sm:h-14 w-auto object-contain" />
                </div>
                <span className="text-zinc-600 text-sm">a další partneři brzy…</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />

      <a
        href="tel:+420725706235"
        className="sm:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-center gap-2 bg-orange-400 text-black font-semibold py-3.5"
        style={{ paddingBottom: "calc(0.875rem + env(safe-area-inset-bottom))" }}
        data-testid="spoluprace-sticky-call"
      >
        <PhoneCall className="w-4 h-4" /> Zavolat nám
      </a>
    </div>
  );
};

export default SpolupracePage;
